import type { MouseEvent } from 'react'
import { useMemo } from 'react'
import type { WOManufacturerMatchStore } from '../wo-manufacturer-match/wo-manufacturer-match-store'
import { useResolvedManufacturerAlias } from '../wo-manufacturer-match/wo-manufacturer-match-store'
import { observer } from 'mobx-react-lite'
import type { MRT_Cell } from 'material-react-table'
import { useManufacturerById } from '../../../../stores/manufacturers-store'
import type { Stores } from '../../../../util/store'
import styles from './registered-manufacturer-cell.module.scss'
import { ManufacturersModalState } from './types'
import { APIModelAliasReference } from '@visorpro/client'
import { useModelsById } from '../../../../stores/models-store'

export interface RegisteredManufacturerCellProps {
    cell: MRT_Cell<APIModelAliasReference, unknown>
    store: WOManufacturerMatchStore
    stores: Stores
    onOpen(e: MouseEvent, state: ManufacturersModalState): void
}

export const RegisteredManufacturerCell =
    observer<RegisteredManufacturerCellProps>(
        ({ cell, store, stores, onOpen }) => {
            const alias = useResolvedManufacturerAlias(cell.row.original, store)
            const manufacturer = useManufacturerById(
                alias?.product_manufacturer_id,
                stores,
            )
            const modelIds = cell.row.original.product_model_ids
            const modelIdsSet = useMemo(() => new Set(modelIds), [modelIds])
            const models = useModelsById(stores, modelIdsSet)
            const modelManufacturers = useMemo(() => {
                const seenIds = new Set<string>()
                const items: Array<{ id: string; name: string }> = []

                if (manufacturer?.id) {
                    seenIds.add(manufacturer.id)
                }

                models.forEach((model) => {
                    const manufacturerId = model.product_manufacturer_id
                    if (!manufacturerId || seenIds.has(manufacturerId)) {
                        return
                    }

                    const modelManufacturer =
                        stores.manufacturers.manufacturersById[manufacturerId]

                    if (!modelManufacturer) {
                        return
                    }

                    seenIds.add(manufacturerId)
                    items.push({
                        id: manufacturerId,
                        name: modelManufacturer.name,
                    })
                })

                return items
            }, [
                manufacturer?.id,
                models,
                stores.manufacturers.manufacturersById,
            ])

            const manufacturerAliasId = alias?.alias_id ?? undefined

            if (!manufacturer && modelManufacturers.length === 0) {
                return null
            }

            return (
                <div className={styles.container}>
                    {manufacturer &&
                        (manufacturerAliasId ? (
                            <span
                                onClick={(e) => {
                                    onOpen(e, {
                                        initial_manufacturer_id:
                                            manufacturer.id,
                                        manufacturer_alias_id:
                                            manufacturerAliasId,
                                    })
                                }}
                                className={styles.pill}>
                                {manufacturer.name}
                            </span>
                        ) : (
                            <span className={styles.pillStatic}>
                                {manufacturer.name}
                            </span>
                        ))}
                    {modelManufacturers.map((item) => {
                        return (
                            <span key={item.id} className={styles.pillStatic}>
                                {item.name}
                            </span>
                        )
                    })}
                </div>
            )
        },
    )
